import {
  StyleParamType,
  wixColorParam,
  wixFontParam,
  createStylesParams,
  IStyleParam,
  ISiteTextPreset,
} from '@wix/tpa-settings';
import { CustomCssVarsFn } from '@wix/yoshi-flow-editor';
import { generateCustomCssVars } from './stylesParamsUtils';

export enum StickyStyles {
  STICKY = 'sticky',
  UNSET = 'unset',
}
export enum AlignmentOptions {
  BOTTOM = 'bottom',
  TOP = 'top',
  CENTER = 'center',
}

export enum DisplayStyles {
  BLOCK = 'block',
  NONE = 'none',
}

export type IStylesParams = IStyleParamsBoolean &
  IStylesParamsNumbers &
  IStyleParamsColors &
  IStyleParamsFonts &
  IStyleParamsString &
  IStyleParamsAlignment;

export type IStyleParamsBoolean = {
  headerTitleVisibility: StyleParamType.Boolean;
  headerServiceFilterVisibility: StyleParamType.Boolean;
  headerStaffFilterVisibility: StyleParamType.Boolean;
  headerLocationFilterVisibility: StyleParamType.Boolean;
  headerTimezoneSelectionVisibility: StyleParamType.Boolean;
  sessionServiceImageVisibility: StyleParamType.Boolean;
  sessionDurationVisibility: StyleParamType.Boolean;
  sessionStaffImageVisibility: StyleParamType.Boolean;
  sessionLocationVisibility: StyleParamType.Boolean;
  sessionStaffMemberVisibility: StyleParamType.Boolean;
  sessionPriceVisibility: StyleParamType.Boolean;
  sessionNumberOfSpotsLeftVisibility: StyleParamType.Boolean;
  canBookFromDailyAgenda: StyleParamType.Boolean;
};
export type IStyleParamsAlignment = {
  itemsAlignment: StyleParamType.String;
};

export type IStyleParamsFonts = {
  headerTitleFont: StyleParamType.Font;
  datePickerDatesFont: StyleParamType.Font;
  filtersFont: StyleParamType.Font;
  tableDatesFont: StyleParamType.Font;
  tableDayFont: StyleParamType.Font;
  sessionsItemsServiceNameFont: StyleParamType.Font;
  sessionsItemsPrimaryFont: StyleParamType.Font;
  sessionsItemsSecondaryFont: StyleParamType.Font;
  sessionItemsRegularButtonFont: StyleParamType.Font;
  loadMoreButtonFont: StyleParamType.Font;
};

export type IStylesParamsNumbers = {
  generalBorderWidth: StyleParamType.Number;
  generalWidgetCornerRadius: StyleParamType.Number;
  tableDayDividerWidth: StyleParamType.Number;
  tableSessionDividerWidth: StyleParamType.Number;
  sessionItemsCornerRadius: StyleParamType.Number;
  sessionItemsImageCornerRadius: StyleParamType.Number;
  sessionsItemsRegularButtonBorderWidth: StyleParamType.Number;
  sessionsItemsRegularCornerRadius: StyleParamType.Number;
  loadMoreButtonBorderWidth: StyleParamType.Number;
  loadMoreButtonCornerRadius: StyleParamType.Number;
  layoutHeaderSidePadding: StyleParamType.Number;
  layoutHeaderTopPadding: StyleParamType.Number;
  layoutHeaderBottomPadding: StyleParamType.Number;
  layoutImageSize: StyleParamType.Number;
  itemsLayoutTopAndBottomPadding: StyleParamType.Number;
  dayLayoutTopAndBottomPadding: StyleParamType.Number;
  dayLayoutsidePadding: StyleParamType.Number;
  dayLayoutpaddingBetweenSessionsAndDates: StyleParamType.Number;
};

export type IStyleParamsColors = {
  borderOpacityAndColor: StyleParamType.Color;
  headerTitleColor: StyleParamType.Color;
  datePickerDatesColor: StyleParamType.Color;
  selectionAndHighlightColor: StyleParamType.Color;
  datePickerSelectedColor: StyleParamType.Color;
  datePickerDisabledColor: StyleParamType.Color;
  filtersColor: StyleParamType.Color;
  tableDateColor: StyleParamType.Color;
  tableDayColor: StyleParamType.Color;
  tableDayDividerOpacity: StyleParamType.Color;
  tableMobileDayDividerColor: StyleParamType.Color;
  generalBorderOpacityAndColor: StyleParamType.Color;
  tableSessionDividerOpacity: StyleParamType.Color;
  generalWidgetBackgroundColor: StyleParamType.Color;
  sessionItemsBackgroundColor: StyleParamType.Color;
  sessionItemsImageOverlayOpacityAndColor: StyleParamType.Color;
  sessionsItemsButtonColor: StyleParamType.Color;
  sessionsItemsButtonBorderAndOpacityColor: StyleParamType.Color;
  sessionsItemsDisabledButtonBorderAndOpacityColor: StyleParamType.Color;
  sessionsItemsButtonBackgroundColor: StyleParamType.Color;
  sessionsItemsDisabledButtonBackgroundColor: StyleParamType.Color;
  sessionsItemsTextColor: StyleParamType.Color;
  sessionsItemsDisabledTextColor: StyleParamType.Color;
  sessionItemsRegularButtonColor: StyleParamType.Color;
  sessionsItemsRegularButtonBackgroundAndOpacityColor: StyleParamType.Color;
  sessionsItemsRegularButtonBorderAndOpacityColor: StyleParamType.Color;
  loadMoreButtonColor: StyleParamType.Color;
  loadMoreButtonBackgroundAndOpacityColor: StyleParamType.Color;
  loadMoreButtonBorderAndOpacityColor: StyleParamType.Color;
  sessionItemsPrimaryTextColor: StyleParamType.Color;
  sessionItemsServiceNameTextColor: StyleParamType.Color;
  sessionItemsSecondaryTextColor: StyleParamType.Color;
};

export type IStyleParamsString = {
  loadMoreButtonVisibility: StyleParamType.String;
  tableDayDividerVisibility: StyleParamType.String;
  tableSessionDividerVisibility: StyleParamType.String;
  headerSticky: StyleParamType.String;
  dateSticky: StyleParamType.String;
};

export const styleParamsDefinitions: {
  [key in keyof IStylesParams]: IStyleParam;
} = {
  itemsAlignment: {
    type: StyleParamType.String,
    getDefaultValue: () => AlignmentOptions.CENTER,
  },
  generalBorderWidth: {
    type: StyleParamType.Number,
    getDefaultValue: () => 0,
  },
  loadMoreButtonBorderWidth: {
    type: StyleParamType.Number,
    getDefaultValue: () => 1,
  },
  loadMoreButtonCornerRadius: {
    type: StyleParamType.Number,
    getDefaultValue: () => 1,
  },
  layoutHeaderSidePadding: {
    type: StyleParamType.Number,
    getDefaultValue: () => 16,
  },
  layoutHeaderTopPadding: {
    type: StyleParamType.Number,
    getDefaultValue: () => 16,
  },
  layoutHeaderBottomPadding: {
    type: StyleParamType.Number,
    getDefaultValue: () => 16,
  },
  layoutImageSize: {
    type: StyleParamType.Number,
    getDefaultValue: () => 80,
  },
  itemsLayoutTopAndBottomPadding: {
    type: StyleParamType.Number,
    getDefaultValue: () => 16,
  },
  dayLayoutTopAndBottomPadding: {
    type: StyleParamType.Number,
    getDefaultValue: () => 16,
  },
  dayLayoutsidePadding: {
    type: StyleParamType.Number,
    getDefaultValue: () => 16,
  },
  dayLayoutpaddingBetweenSessionsAndDates: {
    type: StyleParamType.Number,
    getDefaultValue: () => 60,
  },
  sessionsItemsRegularButtonBorderWidth: {
    type: StyleParamType.Number,
    getDefaultValue: () => 1,
  },
  tableDayFont: {
    type: StyleParamType.Font,
    getDefaultValue: wixFontParam('Body-M', { size: 16 }),
  },
  loadMoreButtonFont: {
    type: StyleParamType.Font,
    getDefaultValue: wixFontParam('Body-M', { size: 16 }),
  },
  loadMoreButtonColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-fill-primary'),
  },
  loadMoreButtonBackgroundAndOpacityColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-1'),
  },
  loadMoreButtonBorderAndOpacityColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-fill-primary'),
  },
  sessionItemsRegularButtonFont: {
    type: StyleParamType.Font,
    getDefaultValue: wixFontParam('Body-M', { size: 16 }),
  },
  sessionItemsRegularButtonColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-fill-background-primary', 1),
  },
  sessionItemsPrimaryTextColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-text-primary'),
  },
  sessionItemsServiceNameTextColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-text-primary'),
  },
  sessionItemsSecondaryTextColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-text-secondary'),
  },
  sessionsItemsRegularButtonBackgroundAndOpacityColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-text-primary'),
  },
  sessionsItemsRegularButtonBorderAndOpacityColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-text-primary'),
  },
  sessionsItemsRegularCornerRadius: {
    type: StyleParamType.Number,
    getDefaultValue: () => 1,
  },
  sessionsItemsTextColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-fill-background-primary'),
  },
  sessionsItemsDisabledButtonBorderAndOpacityColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('button-color-fill-primary-disabled'),
  },
  sessionsItemsDisabledTextColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-fill-background-primary'),
  },
  sessionsItemsButtonColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-1'),
  },
  sessionsItemsButtonBackgroundColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-text-primary'),
  },
  sessionsItemsDisabledButtonBackgroundColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('button-color-fill-primary-disabled'),
  },
  sessionsItemsButtonBorderAndOpacityColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-1'),
  },
  sessionsItemsSecondaryFont: {
    type: StyleParamType.Font,
    getDefaultValue: ({
      textPresets,
      isMobile,
    }: {
      textPresets: ISiteTextPreset;
      isMobile: boolean;
    }) =>
      wixFontParam('Body-M', {
        size: isMobile ? 14 : 16,
      })({ textPresets }),
  },
  sessionsItemsServiceNameFont: {
    type: StyleParamType.Font,
    getDefaultValue: wixFontParam('Page-title', { size: 28 }),
  },
  sessionsItemsPrimaryFont: {
    type: StyleParamType.Font,
    getDefaultValue: ({
      textPresets,
      isMobile,
    }: {
      textPresets: ISiteTextPreset;
      isMobile: boolean;
    }) =>
      wixFontParam('Body-M', {
        size: isMobile ? 14 : 16,
      })({ textPresets }),
  },
  tableDayColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5'),
  },
  sessionItemsBackgroundColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-10'),
  },
  sessionItemsImageOverlayOpacityAndColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5'),
  },
  sessionItemsCornerRadius: {
    type: StyleParamType.Number,
    getDefaultValue: () => 0,
  },
  sessionItemsImageCornerRadius: {
    type: StyleParamType.Number,
    getDefaultValue: () => 0,
  },
  tableDatesFont: {
    type: StyleParamType.Font,
    getDefaultValue: wixFontParam('Body-M', { size: 20 }),
  },
  tableDateColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-text-primary'),
  },
  tableDayDividerOpacity: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-text-primary', 100),
  },
  tableMobileDayDividerColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-text-primary', 0.06),
  },
  generalBorderOpacityAndColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-line'),
  },
  tableSessionDividerOpacity: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-line', 0.3),
  },
  generalWidgetBackgroundColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-fill-background-primary'),
  },
  generalWidgetCornerRadius: {
    type: StyleParamType.Number,
    getDefaultValue: () => 0,
  },
  tableDayDividerWidth: {
    type: StyleParamType.Number,
    getDefaultValue: () => 1,
  },
  tableSessionDividerWidth: {
    type: StyleParamType.Number,
    getDefaultValue: () => 1,
  },
  borderOpacityAndColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-8', 0.2),
  },
  headerTitleFont: {
    type: StyleParamType.Font,
    getDefaultValue: wixFontParam('Page-title', { size: 24 }),
  },
  headerTitleColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-text-primary'),
  },
  datePickerDatesFont: {
    type: StyleParamType.Font,
    getDefaultValue: wixFontParam('Body-M', { size: 14 }),
  },
  datePickerDatesColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-text-primary'),
  },
  selectionAndHighlightColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-text-primary'),
  },
  datePickerDisabledColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-disabled', 1),
  },
  datePickerSelectedColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-fill-background-primary', 100),
  },
  filtersFont: {
    type: StyleParamType.Font,
    getDefaultValue: wixFontParam('Body-M', { size: 14 }),
  },
  filtersColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-text-secondary'),
  },
  loadMoreButtonVisibility: {
    type: StyleParamType.String,
    getDefaultValue: () => DisplayStyles.BLOCK,
  },
  headerSticky: {
    type: StyleParamType.String,
    getDefaultValue: () => StickyStyles.STICKY,
  },
  dateSticky: {
    type: StyleParamType.String,
    getDefaultValue: () => StickyStyles.STICKY,
  },
  headerTitleVisibility: {
    type: StyleParamType.Boolean,
    getDefaultValue: () => true,
  },
  headerServiceFilterVisibility: {
    type: StyleParamType.Boolean,
    getDefaultValue: () => true,
  },
  headerStaffFilterVisibility: {
    type: StyleParamType.Boolean,
    getDefaultValue: () => true,
  },
  headerLocationFilterVisibility: {
    type: StyleParamType.Boolean,
    getDefaultValue: () => true,
  },
  headerTimezoneSelectionVisibility: {
    type: StyleParamType.Boolean,
    getDefaultValue: () => false,
  },
  tableDayDividerVisibility: {
    type: StyleParamType.String,
    getDefaultValue: () => DisplayStyles.BLOCK,
  },
  tableSessionDividerVisibility: {
    type: StyleParamType.String,
    getDefaultValue: () => DisplayStyles.BLOCK,
  },
  sessionServiceImageVisibility: {
    type: StyleParamType.Boolean,
    getDefaultValue: () => true,
  },
  sessionDurationVisibility: {
    type: StyleParamType.Boolean,
    getDefaultValue: () => true,
  },
  sessionStaffImageVisibility: {
    type: StyleParamType.Boolean,
    getDefaultValue: () => true,
  },
  sessionLocationVisibility: {
    type: StyleParamType.Boolean,
    getDefaultValue: () => true,
  },
  sessionStaffMemberVisibility: {
    type: StyleParamType.Boolean,
    getDefaultValue: () => true,
  },
  sessionPriceVisibility: {
    type: StyleParamType.Boolean,
    getDefaultValue: () => undefined,
  },
  sessionNumberOfSpotsLeftVisibility: {
    type: StyleParamType.Boolean,
    getDefaultValue: () => true,
  },
  canBookFromDailyAgenda: {
    type: StyleParamType.Boolean,
    getDefaultValue: () => true,
  },
};

export default createStylesParams<IStylesParams>(styleParamsDefinitions);

export const customCssVars: CustomCssVarsFn = (args) =>
  generateCustomCssVars(args);
